import { AddLayerObject, Map } from 'maplibre-gl';

import {
  CUSTOMIZE_PREPENDED_ID,
  CustomizedLayerIds,
} from '@/features/customizeMyLand/layers';
import { mapboxStyles } from '@/features/customizeMyLand/layers/drawToolMapboxLayers';
import { AcresMapboxLayerIds } from '@/mapbox-layers';

import getMapboxDrawStyles from '../../features/draw/styles';
import Logger from '../logger';

/**
 * All of the draw styles ids in the hot and cold layers
 */
const getDrawLayers = [...getMapboxDrawStyles()]
  .map((item) => [`${item.id}.hot`, `${item.id}.cold`])
  .flat();

const customizeLayers = mapboxStyles
  .map((item) => [`${item.id}.hot`, `${item.id}.cold`])
  .flat();

// Needed for embeds
const customizedLayerIdsMap = Object.values(CustomizedLayerIds);

// Layers listed from lowest to highest
export const priorityLayers: string[] = [
  AcresMapboxLayerIds.SELECTION_DOT,
  AcresMapboxLayerIds.CLU,
  AcresMapboxLayerIds.CLU_FILL,
  AcresMapboxLayerIds.CLU_LABELS,
  AcresMapboxLayerIds.ACTIVE_CLU,
  AcresMapboxLayerIds.CLU_CENTROIDS,
  AcresMapboxLayerIds.CLU_CENTROID_INTERSECTION_LABEL_CENTROIDS,
  AcresMapboxLayerIds.BASE_COUNTRY_LAYER,
  AcresMapboxLayerIds.BASE_STATE_LAYER,
  AcresMapboxLayerIds.BASE_STATE_FILL_LAYER,
  AcresMapboxLayerIds.STATE_FILL,
  AcresMapboxLayerIds.STATE_HIGHLIGHT,
  AcresMapboxLayerIds.STATE_OUTLINE,
  AcresMapboxLayerIds.STATE_OUTLINE_PERMANENT,
  AcresMapboxLayerIds.COUNTY_FILL,
  AcresMapboxLayerIds.COUNTY,
  AcresMapboxLayerIds.COUNTY_HIGHLIGHT,
  AcresMapboxLayerIds.COUNTY_LABEL,
  AcresMapboxLayerIds.COUNTY_OUTLINE,
  AcresMapboxLayerIds.COUNTY_FILL_ALT,
  AcresMapboxLayerIds.TOWNSHIP_LABELS,
  AcresMapboxLayerIds.TOWNSHIP_LINE,
  AcresMapboxLayerIds.TOWNSHIP_FILL,
  AcresMapboxLayerIds.TOWNSHIP_OUTLINE,
  AcresMapboxLayerIds.TOWNSHIP_OUTLINE_PERMANENT,
  AcresMapboxLayerIds.SECTION_OUTLINE,
  AcresMapboxLayerIds.SECTION_LINE,
  AcresMapboxLayerIds.SECTION_FILL,
  AcresMapboxLayerIds.PARCEL,
  AcresMapboxLayerIds.PARCEL_ID,
  AcresMapboxLayerIds.PARCEL_PARTIAL,
  AcresMapboxLayerIds.PARCEL_OWNER_LABEL,
  AcresMapboxLayerIds.PARCEL_OUTLINE_HOVER,
  AcresMapboxLayerIds.SECTION_LABELS,
  AcresMapboxLayerIds.LOCATION,
  AcresMapboxLayerIds.ADVANCEDSEARCH_DOT,
  AcresMapboxLayerIds.SELECTION_OUTLINE,
  AcresMapboxLayerIds.SELECTION_FILL,
  ...getDrawLayers,
  AcresMapboxLayerIds.COURTHOUSE,
  AcresMapboxLayerIds.COMP_ACRETRADER,
  AcresMapboxLayerIds.COMP_SALE,
  AcresMapboxLayerIds.COMP_AUCTION,
  AcresMapboxLayerIds.COMP_REJECTED,
  AcresMapboxLayerIds.POWER_LINES,
  AcresMapboxLayerIds.SUBSTATIONS,
  AcresMapboxLayerIds.ELEVATION,
  AcresMapboxLayerIds.ELEVATION_COLOR,
  AcresMapboxLayerIds.ELEVATION_CONTOUR,
  AcresMapboxLayerIds.ELEVATION_CONTOUR_WIDE,
  AcresMapboxLayerIds.EMBED,
  AcresMapboxLayerIds.BOUNDARY,
  AcresMapboxLayerIds.BOUNDARY_OUTLINE,
  AcresMapboxLayerIds.ADVANCEDSEARCH_FILL,
  AcresMapboxLayerIds.ADVANCEDSEARCH_OUTLINE,
  AcresMapboxLayerIds.ADVANCEDSEARCH_DOT,
  AcresMapboxLayerIds.SIBLING_PROPERTY_TAX_OUTLINE,
  AcresMapboxLayerIds.SIBLING_PROPERTY_TAX_FILL,
  AcresMapboxLayerIds.CROP,
  AcresMapboxLayerIds.NDVI,
  AcresMapboxLayerIds.CROP_HISTORY,
  AcresMapboxLayerIds.SURFACE_WATER,
  AcresMapboxLayerIds.HISTORICAL_IMAGERY,
  AcresMapboxLayerIds.WATER,
  AcresMapboxLayerIds.SOIL,
  AcresMapboxLayerIds.POWER_LINES,
  AcresMapboxLayerIds.SUBSTATIONS,
  AcresMapboxLayerIds.TIMBER_SITE_INDEX,
  AcresMapboxLayerIds.TIMBER_SITE_INDEX_LINE,
  AcresMapboxLayerIds.TIMBER_MILLS,
  AcresMapboxLayerIds.FLOOD,
  AcresMapboxLayerIds.WETLANDS,
  AcresMapboxLayerIds.FLOOD_LABELS_FILL,
  AcresMapboxLayerIds.FLOOD_META_LABELS,
  AcresMapboxLayerIds.FLOOD_LABELS,
  AcresMapboxLayerIds.CA_WATER_DISTRICTS,
  AcresMapboxLayerIds.CA_WATER_DISTRICTS_OUTLINE,
  AcresMapboxLayerIds.CA_SGMA,
  AcresMapboxLayerIds.CA_SGMA_OUTLINE,
  AcresMapboxLayerIds.CA_GSA,
  AcresMapboxLayerIds.CA_GSA_OUTLINE,
  AcresMapboxLayerIds.CA_ADJUDICATED_BASINS,
  AcresMapboxLayerIds.CA_ADJUDICATED_BASINS_OUTLINE,
  AcresMapboxLayerIds.NE_NRD,
  AcresMapboxLayerIds.NE_NRD_OUTLINE,
  AcresMapboxLayerIds.FARM_RENT,
  AcresMapboxLayerIds.MORTGAGE,
  AcresMapboxLayerIds.USER_COMPS,
  AcresMapboxLayerIds.COURTHOUSE_COMPS,
  AcresMapboxLayerIds.REAL_ESTATE,
  AcresMapboxLayerIds.PORTFOLIO_CENTROIDS,
  AcresMapboxLayerIds.PORTFOLIO_SAVED_SELECTIONS,
  AcresMapboxLayerIds.PORTFOLIO_COMPS,
  ...customizedLayerIdsMap,
  ...customizeLayers,
];

// This function is internal to support these functions
function moveUpPriorityLayers(map: Map) {
  const copyOfPriotiyLayers = [...priorityLayers];
  // Adding cog-cdl layers to the priority layers
  const cogCdlLayers = map
    .getStyle()
    ?.layers.map((item) => item.id)
    .filter((id) => id.includes('cog-cdl'));

  const indexToInsertAt = copyOfPriotiyLayers.findIndex((id) =>
    id.includes(CUSTOMIZE_PREPENDED_ID)
  );

  if (cogCdlLayers.length > 0) {
    copyOfPriotiyLayers.splice(indexToInsertAt, 0, ...cogCdlLayers);
  }

  // Adding customize text layers to the priority layers
  const customizeTextLayers = map
    .getStyle()
    ?.layers.map((item) => item.id)
    .filter((id) => id.includes(`${CUSTOMIZE_PREPENDED_ID}-text`));

  if (customizeTextLayers.length > 0) {
    copyOfPriotiyLayers.push(...customizeTextLayers);
  }

  copyOfPriotiyLayers.forEach((layer) => {
    if (map.getLayer(layer)) {
      map.moveLayer(layer);
    }
  });
}

/**
 * Moves a layer above the others on the map
 * @param map The map
 * @param layerId The id of the layer to move to the top
 * @param preservePriorityLayers  Set to true of the priority layers should remain on top
 */
export function moveLayerToTop(
  map: Map,
  layerId: string,
  preservePriorityLayers = false // Default false for backwards compatibility, ideally should be true
) {
  if (map.getLayer(layerId)) {
    map.moveLayer(layerId);
  }

  if (preservePriorityLayers) {
    moveUpPriorityLayers(map);
  }
}

const addLayer = async (
  map: Map,
  layers: AddLayerObject[],
  label?: string,
  shouldRemoveSource = true
) => {
  try {
    layers.forEach((layer) => {
      if (map.getLayer(layer.id)) map.removeLayer(layer.id);
      if (shouldRemoveSource && map.getSource(layer.id))
        map.removeSource(layer.id);

      map.addLayer(layer, label);
    });

    // Always keep the parcel highlight at the top
    moveUpPriorityLayers(map);
  } catch (error) {
    Logger.error(error);
  }
};

export default addLayer;
